import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useCallback } from "react";
import { Grid, Typography, Box } from "@material-ui/core";
import { DataTable } from "@ploy-ui/core";
import { calculate } from "./utils";
import { SearchForm } from "./SearchForm";
import { ExpandableRegistrationObject } from "./ExpandableRegistrationObject";
import Check from "@material-ui/icons/Check";
import { FormattedDate, FormattedNumber, FormattedMessage, useIntl } from "react-intl";
import { messages } from "./messages";
export const KmReading = ({ search, register, getCalculationDetails, vendorSearch, sendMessage, className, options, isInternal }) => {
    const [rows, setRows] = useState([]);
    const intl = useIntl();
    const handleSearch = useCallback(async ({ carRegistrationNo, fromDate, toDate, vendor }, actions) => {
        try {
            const [orgNo = "", backOfficeId = ""] = vendor ? vendor.split("|") : [];
            const { list, message, success } = await search({
                carRegistrationNo,
                fromDate,
                toDate,
                vendor: orgNo,
                backofficeId: backOfficeId
            });
            const rows = list.sort((x, y) => Number(x.contractEndDate) - Number(y.contractEndDate));
            setRows(rows);
            actions.setStatus({ success, message });
        }
        catch (err) {
            actions.setStatus({
                error: intl.formatMessage(messages.genericError)
            });
        }
        actions.setSubmitting(false);
    }, [intl, search]);
    const handleGetCalculationDetails = useCallback(async (carRegistrationNo, vendor) => {
        try {
            const result = await getCalculationDetails(carRegistrationNo, vendor);
            return result;
        }
        catch (err) {
            console.log(`Failed getting data for ${carRegistrationNo} and vendor ${vendor}`, err);
        }
    }, [getCalculationDetails]);
    const handleRegister = useCallback(async ({ reading, readingDate, contractNo, regNo, limit, price, customerName, mvaPercentage, deliveryDate, allowedKm, distanceKm, emailComment, objectType }, actions) => {
        try {
            let amount = reading - allowedKm;
            const calculation = calculate({
                price,
                amount,
                mvaPercentage,
                allowedDeviation: options.allowedDeviation
            });
            const invoiceAmount = calculation.net;
            if (reading > limit && options.doCalculation) {
                // Handle frontend input error
                const params = {
                    EventCode: "KmRead",
                    Source: "kmReading",
                    DtInput: {
                        ReadKilometer: reading,
                        ReadDate: readingDate,
                        NetValue: calculation.net,
                        VatValue: calculation.vat,
                        GrossValue: calculation.gross,
                        ContractNumber: contractNo,
                        RegistrationNumber: regNo
                    }
                };
                const messageSent = await sendMessage(params);
                if (messageSent && messageSent.success) {
                    actions.setSubmitting(false);
                }
                else {
                    actions.setStatus({
                        error: intl.formatMessage(messages.sendMessageError)
                    });
                }
                return;
            }
            const { kmReading, message, success, isApproved } = await register({
                contractNo,
                reading,
                readingDate,
                priceExtraKm: price,
                customerName: customerName,
                registrationNo: regNo,
                invoiceAmount,
                objectType,
                deliveryDate,
                allowedKm,
                distanceKm,
                emailComment
            });
            if (!success) {
                actions.setStatus({
                    error: intl.formatMessage(messages.registerError)
                });
                return;
            }
            setRows(list => list.map(x => x.contractNo === contractNo
                ? {
                    ...x,
                    registeredKilometer: kmReading,
                    isApproved: isApproved || true
                }
                : x));
            actions.setStatus({ success, message });
        }
        catch (err) {
            actions.setStatus({
                error: intl.formatMessage(messages.genericError)
            });
        }
        actions.setSubmitting(false);
    }, [
        options.allowedDeviation,
        options.doCalculation,
        register,
        sendMessage,
        intl
    ]);
    return (_jsxs(Grid, { className: className, container: true, spacing: 3, children: [_jsx(Grid, { xs: 12, item: true, children: _jsxs(Grid, { container: true, spacing: 3, justifyContent: "space-between", children: [_jsx(Grid, { xs: 12, md: 8, item: true, children: _jsx(Typography, { variant: "h3", children: _jsx(FormattedMessage, { id: "km_reading.header", defaultMessage: "Mileage reading", values: {
                                        numContracts: rows.length
                                    } }) }) }), _jsx(Grid, { xs: 12, item: true, children: _jsx(SearchForm, { onSearch: handleSearch, isInternal: isInternal, vendorSearch: vendorSearch, hideDateInput: options.hideDateInput }) })] }) }), _jsx(Grid, { xs: 12, item: true, children: rows.length > 0 && (_jsxs(_Fragment, { children: [_jsxs(Grid, { container: true, children: [_jsx(Grid, { item: true, xs: 12, children: _jsx(Typography, { variant: "h6", align: "right", children: _jsx(FormattedMessage, { id: "km_reading.contracts.message", defaultMessage: "Number of contracts: {numContracts}", values: {
                                                numContracts: rows.length
                                            } }) }) }), options.doCalculation ? (_jsx(Grid, { item: true, xs: 12, children: _jsx(Typography, { variant: "h6", align: "right", children: _jsx(FormattedMessage, { id: "km_reading.total_residual.message", defaultMessage: "Total residual value amount: {total, number, currency}", values: {
                                                total: rows
                                                    .map(x => x.valueAmount)
                                                    .reduce((a, b) => a + b, 0)
                                            } }) }) })) : null] }), _jsx(DataTable, { options: {
                                search: false,
                                showTitle: false,
                                pageSize: 10,
                                paging: rows.length > 10,
                                pageSizeOptions: [10, 15, 20],
                                emptyRowsWhenPaging: false,
                                toolbar: false
                            }, style: { minHeight: 0 }, onRowClick: (event, rowData, togglePanel) => togglePanel && togglePanel(), columns: [
                                {
                                    title: "Reg.nummer",
                                    field: "registrationNo",
                                    headerStyle: { paddingRight: 0 }
                                },
                                {
                                    title: "Avtalenummer",
                                    field: "contractNo",
                                    sorting: false,
                                    headerStyle: { paddingRight: 0 }
                                },
                                {
                                    title: "Kunde",
                                    field: "customerName",
                                    headerStyle: { paddingRight: 0 }
                                },
                                {
                                    title: "Modell/Type",
                                    field: "description",
                                    headerStyle: { paddingRight: 0 }
                                },
                                {
                                    title: "Restverdi",
                                    field: "valueAmount",
                                    render: row => (_jsx(FormattedNumber, { value: row.valueAmount, format: "currency" })),
                                    headerStyle: { paddingRight: 0 }
                                },
                                {
                                    title: "Utløpsdato",
                                    field: "contractEndDate",
                                    render: row => _jsx(FormattedDate, { value: row.contractEndDate }),
                                    headerStyle: { paddingRight: 0 }
                                },
                                {
                                    title: "Avlest",
                                    field: "registeredKilometer",
                                    render: rowData => !options.canEditUntilApproved ? (rowData.registeredKilometer ? (_jsx(Check, {})) : null) : rowData.isApproved ? (_jsx(Check, {})) : null,
                                    sorting: false,
                                    headerStyle: { paddingRight: 0 }
                                }
                            ], data: rows, detailPanel: ({ rowData }) => (_jsx(Box, { p: 2, children: _jsx(ExpandableRegistrationObject, { handleRegister: handleRegister, onExpand: handleGetCalculationDetails, options: options, expanded: rows.length === 1 || undefined, ...rowData }, rowData.contractNo) })) })] })) })] }));
};
//# sourceMappingURL=KmReading.js.map